import React from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Building2, BadgeCheck, Check, SlidersHorizontal, Mail, Phone, Lock, Star, FileText, Copy, ChevronDown, TrendingUp } from 'lucide-react';
// Platform Icons
const TTIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3.5 h-3.5 inline-block" data-id="element-1185" data-mp="3b0mh3cl40ytw" />;
const IGIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3.5 h-3.5 inline-block" data-id="element-1186" data-mp="7jnq0ij94a5x" />;
const YTIco = () => <svg className="w-4 h-4 inline-block text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor" aria-label="YouTube" data-id="element-1187" data-mp="3tv1fazzlkngo">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-1188" data-mp="qfvpt6thvstu" />
  </svg>;
// A window chrome header (traffic-light dots) instead of the app sidebar/tab menu.
const WindowBar = ({
  title,
  approved = false
}: {
  title: string;
  approved?: boolean;
}) => <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/70" data-id="element-1189" data-mp="2vk174zfh3bux">
    <div className="flex items-center gap-1.5" data-id="element-1190" data-mp="3a975ymaef02a">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" data-id="element-1191" data-mp="35cgphxuom2gc" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" data-id="element-1192" data-mp="35e9wje97ioov" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" data-id="element-1193" data-mp="3jb6ltb9ofy3n" />
    </div>
    <span className="ml-2 text-xs font-semibold text-gray-500" data-id="element-1194" data-mp="3ecdfq3wpz9di">{title}</span>
    {approved && <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[9px] font-semibold uppercase tracking-wider" data-id="element-1195" data-mp="1owvzpakgvi1k">
        <Lock size={8} data-id="element-1196" data-mp="3k0tyaj1gl206" /> Approved Access
      </span>}
  </div>;
// Reusable float animation for the small overlapping cards.
const floatY = (delay = 0, distance = 8) => ({
  animate: {
    y: [0, -distance, 0]
  },
  transition: {
    duration: 4,
    repeat: Infinity,
    ease: 'easeInOut' as const,
    delay
  }
});
// 1) ROSTER DATABASE — a card-grid gallery of creators (distinct from the homepage table),
// with a floating live-filters chip and a floating agency-contact chip.
export const LayeredRosterDatabaseMockup = () => {
  const creators = [{
    name: 'Michelle Brown',
    cat: 'Fashion',
    followers: '2.2M',
    photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=160&h=160'
  }, {
    name: 'Elena Rodriguez',
    cat: 'Beauty',
    followers: '1.2M',
    photo: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=160&h=160'
  }, {
    name: 'Sarah Jenkins',
    cat: 'Lifestyle',
    followers: '890K',
    photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=160&h=160'
  }, {
    name: 'Aisha Kohl',
    cat: 'Fitness',
    followers: '640K',
    photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=160&h=160'
  }];
  return <div className="relative h-[440px] w-full" data-id="element-1197" data-mp="2xsf07vrqqvhh">
      <div className="absolute -inset-6 bg-gradient-to-tr from-zano-pink/10 via-zano-lavender/20 to-sky-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1198" data-mp="2xr54hd8d1ze0" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1199" data-mp="2p04q0eguiq08">
        <WindowBar title="Roster Database" approved data-id="element-1200" data-mp="2jzyxkw2r4zh3" />
        {/* search + filter chips */}
        <div className="p-4 pb-3" data-id="element-1201" data-mp="3hgnxgk855rnu">
          <div className="flex items-center gap-2" data-id="element-1202" data-mp="3hhqm0lhzri0l">
            <div className="relative flex-1" data-id="element-1203" data-mp="3cp5whw64br2v">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1204" data-mp="3coe938moczno" />
              <input type="text" placeholder="Search 6,000+ creators" className="w-full pl-8 pr-2 py-2 border border-gray-200 rounded-lg text-xs outline-none bg-gray-50" data-id="element-1205" data-mp="319st65d16u1u" />
            </div>
            <button className="flex items-center gap-1 border border-zano-pink/30 bg-zano-blush text-zano-pink px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap" data-id="element-1206" data-mp="2s8uum2ld3eqn">
              <SlidersHorizontal size={12} data-id="element-1207" data-mp="3jyh9sf4hed7t" /> Filters
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5" data-id="element-1208" data-mp="367b3o06p5hby">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-1209" data-mp="3b7gzztnwvs0d">
              <BadgeCheck size={10} data-id="element-1210" data-mp="2qce5akhl0q3" /> Exclusive only
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200" data-id="element-1211" data-mp="7jf9no8tqk8x">
              Fashion
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200" data-id="element-1212" data-mp="cuj4ehgu2coq">
              100K+ followers
            </span>
          </div>
        </div>
        {/* card gallery */}
        <div className="px-4 pb-4 grid grid-cols-2 gap-3 overflow-hidden" data-id="element-1213" data-mp="3u3fs4mv7v4rz">
          {creators.map((c, i) => <div key={i} className="border border-gray-100 rounded-xl p-3 bg-white hover:border-zano-pink/30 transition-colors" data-id="element-1214" data-mp="3q2m7q3gzg83u" data-mpi={i}>
              <div className="flex items-center gap-2.5" data-id="element-1215" data-mp="2xed6384rr12">
                <img src={c.photo} alt={c.name} className="w-11 h-11 rounded-full object-cover" data-id="element-1216" data-mp="2vti8chwm62o" />
                <div className="min-w-0" data-id="element-1217" data-mp="1ed3415oi9a75">
                  <p className="text-xs font-semibold text-gray-900 truncate" data-id="element-1218" data-mp="1ecbgmi52airy">
                    {c.name}
                  </p>
                  <p className="text-[10px] text-gray-500" data-id="element-1219" data-mp="1qeuj5jvpjrz7">{c.cat}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2.5" data-id="element-1220" data-mp="1ve77k928973w">
                <span className="flex items-center gap-1 text-[10px] font-medium text-gray-600" data-id="element-1221" data-mp="1vc4ipsct9k0r">
                  <TTIco data-id="element-1222" data-mp="20axu9f8jf0ya" /> {c.followers}
                </span>
                <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-600" data-id="element-1223" data-mp="12ql2sxh9jwfg">
                  <BadgeCheck size={9} data-id="element-1224" data-mp="17owbv5zqi2wm" /> Exclusive
                </span>
              </div>
            </div>)}
        </div>
      </div>
      {/* Floating live-stats chip */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -left-4 sm:-left-6 z-20" data-id="element-1225" data-mp="1hey2o5jh1zf8">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-1226" data-mp="cfj1ld5fcik9">
          <div className="flex items-center gap-1.5 mb-2" data-id="element-1227" data-mp="7i9brwe4i4vw">
            <TrendingUp size={13} className="text-zano-pink" data-id="element-1228" data-mp="2gaacs1y1i1l" />
            <p className="text-[11px] font-bold text-gray-900" data-id="element-1229" data-mp="3ukmbdb6c5ir0">Live stats</p>
            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-1230" data-mp="3umpaqltdoo8e" />
          </div>
          <div className="space-y-1.5" data-id="element-1231" data-mp="3kv2qy12z5gu1">
            <div className="flex justify-between text-[10px]" data-id="element-1232" data-mp="3kw4cx0lj3nqj">
              <span className="text-gray-500" data-id="element-1233" data-mp="3fvztb1pbqcw3">Avg. views</span>
              <span className="font-semibold text-gray-900" data-id="element-1234" data-mp="3fxt50vesnnay">142K</span>
            </div>
            <div className="flex justify-between text-[10px]" data-id="element-1235" data-mp="3awdjl28pfes1">
              <span className="text-gray-500" data-id="element-1236" data-mp="36us0v09ho69x">Eng. rate</span>
              <span className="font-semibold text-gray-900" data-id="element-1237" data-mp="36xnwghxv6iv7">5.3%</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
      {/* Floating agency-contact chip */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-5 -right-4 sm:-right-6 z-20" data-id="element-1238" data-mp="13kx1nx8c2k5d">
        <motion.div {...floatY(0.9)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-52" data-id="element-1239" data-mp="knvv47z4sbzb">
          <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-1240" data-mp="awrel3tt4j2d">
            Talent manager
          </p>
          <div className="flex items-center justify-between gap-2 bg-zano-blush/60 border border-zano-pink/20 rounded-md px-2 py-1.5" data-id="element-1241" data-mp="3t3xd7ik4x6c9">
            <span className="flex items-center gap-1.5 text-[10px] font-medium text-zano-charcoal truncate" data-id="element-1242" data-mp="10o5fe0jfk5x">
              <Mail size={11} className="text-zano-pink shrink-0" data-id="element-1243" data-mp="39gu805br7vb0" />
              john@abctalentagency.com
            </span>
            <Copy size={10} className="text-gray-400 shrink-0" data-id="element-1244" data-mp="3ngvjeepnal4q" />
          </div>
        </motion.div>
      </motion.div>
    </div>;
};
// 2) A-LIST TALENT — a focused search result surfacing one premium profile (generic data),
// with a floating "A-List verified" badge that floats.
export const LayeredAListMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1245" data-mp="3sf6s2m47epld">
      <div className="absolute -inset-6 bg-gradient-to-tr from-amber-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1246" data-mp="a1h1zjwl2r8b" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1247" data-mp="60ndopf8baew">
        <WindowBar title="Roster Database" approved data-id="element-1248" data-mp="f0tqe933s6d0" />
        <div className="p-4" data-id="element-1249" data-mp="f24uyb4dhi56">
          {/* search bar showing a query */}
          <div className="relative w-full" data-id="element-1250" data-mp="11f6xbn719ugg">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zano-pink" data-id="element-1251" data-mp="11h1ldadlq2or" />
            <div className="w-full pl-9 pr-3 py-2.5 border-2 border-zano-pink/40 rounded-lg text-sm font-medium text-gray-900 bg-white shadow-sm" data-id="element-1252" data-mp="sm3oxtb8w7so">
              Michelle Brown
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2 mb-3" data-id="element-1253" data-mp="1ke2sp1umoevv">1 result</p>
          {/* the premium profile result */}
          <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-1254" data-mp="1gdixi3tm7lcd">
            <div className="p-4 flex items-center gap-3 border-b border-gray-100" data-id="element-1255" data-mp="1bcbpc3nk8k56">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=120&h=120" alt="Michelle Brown" className="w-14 h-14 rounded-full object-cover" data-id="element-1256" data-mp="16ea3jayr26q0" />
              <div className="min-w-0" data-id="element-1257" data-mp="3r2xvmm92rhl5">
                <h3 className="text-base font-bold text-gray-900 leading-tight" data-id="element-1258" data-mp="3h0kgb6udjc0b">
                  Michelle Brown
                </h3>
                <div className="flex items-center gap-2 mt-1" data-id="element-1259" data-mp="3m0gir1nfavdr">
                  <span className="flex items-center gap-1 text-[11px] font-medium text-gray-600" data-id="element-1260" data-mp="3qyrw3mcxfo0q">
                    <TTIco data-id="element-1261" data-mp="3voyirgnvoygc" /> 8.4M
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-medium text-gray-600" data-id="element-1262" data-mp="3vqjbr1ujo9c3">
                    <IGIco data-id="element-1263" data-mp="3l62em0iu08n" /> 3.1M
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4" data-id="element-1264" data-mp="8mbv4ju6lni6">
              <div className="flex items-center gap-2 mb-2" data-id="element-1265" data-mp="dj2cdahq2p56">
                <div className="w-7 h-7 bg-gray-900 text-white rounded-md flex items-center justify-center text-[9px] font-bold" data-id="element-1266" data-mp="dhiwhkzzag3x">
                  ABC
                </div>
                <span className="text-sm font-bold text-gray-900" data-id="element-1267" data-mp="2fcjgi46n97v6">
                  ABC Talent Agency
                </span>
                <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold" data-id="element-1268" data-mp="2kbwdhhr8bvbh">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1269" data-mp="27bc0v07hm5sf" />
                  Exclusive
                </span>
              </div>
              <div className="space-y-2 mt-3" data-id="element-1270" data-mp="2m56xn7udc4n9">
                <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1271" data-mp="2h7f1u2qlryds">
                  Talent manager · John Smith
                </p>
                <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5" data-id="element-1272" data-mp="2q94ybmzcdnif">
                  <span className="flex items-center gap-2 text-xs font-medium text-gray-700 truncate" data-id="element-1273" data-mp="34wzpv01m5ltx">
                    <Mail size={12} className="text-gray-400 shrink-0" data-id="element-1274" data-mp="30099eblwco7z" />
                    john@abctalentagency.com
                  </span>
                  <Copy size={12} className="text-gray-400 shrink-0" data-id="element-1275" data-mp="3dwczbspqmaen" />
                </div>
                <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5" data-id="element-1276" data-mp="39v18xkz1hl5e">
                  <span className="flex items-center gap-2 text-xs font-medium text-gray-700 truncate" data-id="element-1277" data-mp="1fn0s15op1p0">
                    <Phone size={12} className="text-gray-400 shrink-0" data-id="element-1278" data-mp="3jl9hylymd7vj" />
                    +1 (555) 018-2934
                  </span>
                  <Copy size={12} className="text-gray-400 shrink-0" data-id="element-1279" data-mp="3oi9osz3amenp" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Floating A-List badge */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-4 -right-3 sm:-right-5 z-20" data-id="element-1280" data-mp="3en81m8skvmnr">
        <motion.div {...floatY(0.3, 6)} className="flex items-center gap-1.5 bg-white rounded-full border border-amber-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.2)] pl-2 pr-3 py-1.5" data-id="element-1281" data-mp="2vu41c4kuubk6">
          <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-1282" data-mp="3u89k7mxrmk12">
            <Star size={12} className="fill-amber-500 text-amber-500" data-id="element-1283" data-mp="3peymqceufflt" />
          </span>
          <span className="text-[11px] font-bold text-amber-700" data-id="element-1284" data-mp="3kg3wipejqoro">
            A-List talent
          </span>
        </motion.div>
      </motion.div>
      {/* Floating quick-book chip */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-2 -left-4 sm:-left-6 z-20" data-id="element-1285" data-mp="3aipqljq91idm">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-40" data-id="element-1286" data-mp="14noe4bzngewb">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1287" data-mp="17a6ygsedi7nt">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1288" data-mp="1c8s08w99wzed" />
            <p className="text-[10px] font-bold text-gray-900" data-id="element-1289" data-mp="1h09y3bg02jv4">
              Fast turnaround
            </p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-1290" data-mp="1lzmlbxas9sy8">
            Direct line to the manager — no middlemen.
          </p>
        </motion.div>
      </motion.div>
    </div>;
};
// 3) CASTING CALLS — a brief composer panel as the base, with a floating incoming-submission
// card and a floating "posted to 400+ agencies" pulse.
export const LayeredCastingCallMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-1291" data-mp="12bvqkn7n2788">
      <div className="absolute -inset-6 bg-gradient-to-tr from-sky-100/40 via-zano-lavender/30 to-zano-pink/10 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1292" data-mp="12covrjqy8oo9" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1293" data-mp="2dpzqea0wqpl7">
        <WindowBar title="New Casting Call" data-id="element-1294" data-mp="ek7u9250ohqn" />
        <div className="p-4 space-y-3 overflow-hidden" data-id="element-1295" data-mp="9my6rs16ug5g">
          <div data-id="element-1296" data-mp="9meoga7mlpqw">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1297" data-mp="9oslgolv9c13">
              Campaign
            </label>
            <div className="mt-1 text-xs text-gray-800 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1298" data-mp="in4hj65i520x">
              Summer Skincare Launch
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-1299" data-mp="3n1uhsxdvifg8">
            <div data-id="element-1300" data-mp="3n35n51myvv9g">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1301" data-mp="3mzhtm2hiv8uq">
                Budget
              </label>
              <div className="mt-1 text-xs text-gray-800 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1302" data-mp="3w20s6ae9yzb6">
                $10k – $20k
              </div>
            </div>
            <div data-id="element-1303" data-mp="33dv6nlfed6aa">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1304" data-mp="33edbv7k1euuc">
                Platforms
              </label>
              <div className="mt-1 flex gap-1.5 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1305" data-mp="1iu9hrngwbyeu">
                <TTIco data-id="element-1306" data-mp="1nodu6m8xkynb" /> <IGIco data-id="element-1307" data-mp="1no446ynvyrhm" />
              </div>
            </div>
          </div>
          <div data-id="element-1308" data-mp="19wpiul6iae7e">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1309" data-mp="19t1mzfdl9fpi">
              Influencer requirements
            </label>
            <div className="mt-1 text-xs text-gray-600 border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-id="element-1310" data-mp="22kldlkcilw1j">
              Beauty · US-based · 100K+ followers
            </div>
          </div>
          <div data-id="element-1311" data-mp="1skaytvau0gk9">
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1312" data-mp="1skuh5d4e96yt">
              Deliverables
            </label>
            <div className="mt-1 text-xs text-gray-600 border border-gray-200 rounded-md px-3 py-2 bg-gray-50 h-11" data-id="element-1313" data-mp="h6760sfzcygs">
              1x TikTok video, 1x IG story with link…
            </div>
          </div>
          <button className="w-full bg-zano-pink text-white rounded-md py-2.5 text-xs font-bold shadow-sm" data-id="element-1314" data-mp="1dslgxz6ehq9n">
            Post casting call
          </button>
        </div>
      </div>
      {/* Floating "posted to agencies" pulse */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-3 sm:-right-6 z-20" data-id="element-1315" data-mp="13vqjljlv5vwy">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-1316" data-mp="1mtjlqjony1ta">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1317" data-mp="1hsdr2gbg04h4">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1318" data-mp="2favxzkvcxiwe" />
            <p className="text-[10px] font-bold text-gray-900" data-id="element-1319" data-mp="2aim4xvyy42pw">
              Posted to 400+ agencies
            </p>
          </div>
          <p className="text-[10px] text-gray-500" data-id="element-1320" data-mp="2itcmfnlmpo9c">
            Submissions rolling in within hours.
          </p>
        </motion.div>
      </motion.div>
      {/* Floating incoming-submission card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.55
    }} className="absolute -bottom-6 -left-4 sm:-left-8 z-30" data-id="element-1321" data-mp="2rqvdp9pv8yto">
        <motion.div {...floatY(1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-56" data-id="element-1322" data-mp="3aio5j0t78dzt">
          <div className="flex items-center gap-2.5 mb-2" data-id="element-1323" data-mp="3kh3zrcniw3dj">
            <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=64&h=64" alt="Submission" className="w-9 h-9 rounded-full object-cover" data-id="element-1324" data-mp="3kiotj020jiac" />
            <div className="min-w-0" data-id="element-1325" data-mp="wsjh78kqjoo8">
              <p className="text-[11px] font-bold text-gray-900 truncate" data-id="element-1326" data-mp="wtkt1f9ksf70">
                New submission
              </p>
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-700" data-id="element-1327" data-mp="11q1j4f0c53mq">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1328" data-mp="16s0n9r3wg4kq" />
                Exclusive · ABC Talent Agency
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center bg-gray-50 rounded-md border border-gray-100 px-2.5 py-1.5" data-id="element-1329" data-mp="1fqcbjmhamqa0">
            <span className="text-[10px] text-gray-500" data-id="element-1330" data-mp="1kivxp7u1qtqf">Quote</span>
            <span className="text-xs font-bold text-green-600" data-id="element-1331" data-mp="1pg5jmdi88n9i">$1,200</span>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};
// 4) TALENT AGENCY DIRECTORY — a leaderboard-style ranked list of generic agencies,
// with a floating agency profile card.
export const LayeredDirectoryMockup = () => {
  const agencies = [{
    name: 'ABC Talent Agency',
    short: 'ABC',
    creators: '895',
    following: '3.2B'
  }, {
    name: 'Nova Management',
    short: 'NM',
    creators: '642',
    following: '1.6B'
  }, {
    name: 'Peak Creators Co.',
    short: 'PC',
    creators: '410',
    following: '1.1B'
  }, {
    name: 'Bright Talent Group',
    short: 'BT',
    creators: '188',
    following: '420M'
  }];
  return <div className="relative h-[440px] w-full" data-id="element-1332" data-mp="1kh8ra0b920c">
      <div className="absolute -inset-6 bg-gradient-to-tr from-purple-100/40 via-zano-lavender/30 to-blue-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-1333" data-mp="1lsdpd5hsft1" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-1334" data-mp="3nxzc3289rup3">
        <WindowBar title="Talent Agency Directory" approved data-id="element-1335" data-mp="pb3hl3i52qbe" />
        <div className="p-4" data-id="element-1336" data-mp="kh8wpwqss5aq">
          <div className="flex items-center justify-between mb-3" data-id="element-1337" data-mp="kg7bizg2i2fa">
            <p className="text-xs font-bold text-gray-900 flex items-center gap-1.5" data-id="element-1338" data-mp="2er76xp4xtadd">
              <Building2 size={13} className="text-zano-pink" data-id="element-1339" data-mp="2irzemdy5v77j" /> 400+ vetted
              agencies
            </p>
            <button className="flex items-center gap-1 border border-gray-200 bg-white px-2 py-1 rounded text-[10px] text-gray-600" data-id="element-1340" data-mp="24zjhftrxy3ej">
              Ranked by reach <ChevronDown size={10} data-id="element-1341" data-mp="1v251dv9xjgmr" />
            </button>
          </div>
          <div className="space-y-2" data-id="element-1342" data-mp="1v0u1i6jlut0x">
            {agencies.map((a, i) => <div key={i} className="flex items-center gap-3 border border-gray-100 rounded-xl px-3 py-2.5 hover:border-zano-pink/30 transition-colors" data-id="element-1343" data-mp="20219o6pntu84" data-mpi={i}>
                <span className="text-xs font-bold text-gray-400 w-4" data-id="element-1344" data-mp="1r13ca79q8kyi">
                  {i + 1}
                </span>
                <div className="w-8 h-8 bg-gray-900 text-white rounded-lg flex items-center justify-center text-[9px] font-bold shrink-0" data-id="element-1345" data-mp="3gdew71exoa58">
                  {a.short}
                </div>
                <div className="min-w-0 flex-1" data-id="element-1346" data-mp="3virf664xx9de">
                  <p className="text-xs font-semibold text-gray-900 truncate" data-id="element-1347" data-mp="3qjybf5fggwqf">
                    {a.name}
                  </p>
                  <p className="text-[10px] text-gray-500" data-id="element-1348" data-mp="868k5zw3msbs">
                    {a.creators} creators
                  </p>
                </div>
                <div className="text-right shrink-0" data-id="element-1349" data-mp="i3mpp4ghhwpb">
                  <p className="text-xs font-bold text-gray-900" data-id="element-1350" data-mp="i2bjyz3haevk">
                    {a.following}
                  </p>
                  <p className="text-[9px] text-gray-400" data-id="element-1351" data-mp="d4a0id254djk">total reach</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Floating agency profile card */}
      <motion.div initial={{
      opacity: 0,
      y: 20,
      x: -10
    }} whileInView={{
      opacity: 1,
      y: 0,
      x: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -bottom-6 -right-4 sm:-right-8 z-20" data-id="element-1352" data-mp="n0llrsjpkdq5">
        <motion.div {...floatY(0.6)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-4 w-60" data-id="element-1353" data-mp="2vc5tqwnwgl9a">
          <div className="flex items-center gap-2.5 mb-3" data-id="element-1354" data-mp="34ahisu94bazm">
            <div className="w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center text-xs font-bold" data-id="element-1355" data-mp="27la342v6jw3m">
              ABC
            </div>
            <div data-id="element-1356" data-mp="2bjzg3ucb3bmq">
              <h4 className="text-sm font-bold text-gray-900 leading-tight" data-id="element-1357" data-mp="2glothgn041dz">
                ABC Talent Agency
              </h4>
              <p className="text-[10px] text-gray-500" data-id="element-1358" data-mp="2ld6ug4p0y1yy">Los Angeles, CA</p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5" data-id="element-1359" data-mp="1nvpw8xynkxy8">
            <span className="flex items-center gap-1.5 text-[10px] font-medium text-gray-700 truncate" data-id="element-1360" data-mp="1su1bxpbmq2od">
              <Mail size={11} className="text-gray-400 shrink-0" data-id="element-1361" data-mp="1la8rmzbw4fus" />
              contact@abctalentagency.com
            </span>
            <Copy size={10} className="text-gray-400 shrink-0" data-id="element-1362" data-mp="1bdvolauggpnd" />
          </div>
        </motion.div>
      </motion.div>
    </div>;
};